import { isMessageAction, type IChat } from "@stringf/services/chat";
import { signedInAccountId } from "./auth";

/**
 * This device's local copy of a room's messages, on IndexedDB: what lets a reload render
 * a room before the network answers. The server stays authoritative: history and sync
 * still fill gaps and overwrite what is stored (ADR 0005 keeps `roomFamily` the live
 * state; this is only where it is read back from at start-up).
 *
 * One database per account (`astring-local:<accountId>`), found from the stored auth state
 * on every call, so every tab follows a sign-out the moment it happens: with no account
 * there is nothing to read or write, and the signing-out tab deletes the database.
 *
 * Every operation is serialised through one queue, so a `wipe` (sign-out) is ordered
 * against the writes before and after it, and none of them throws: a browser without
 * IndexedDB (private mode, an old WebView) just has no local store.
 */
export interface MessageStore {
  /**
   * Upserts messages by id; the caller says which copy is current. Action frames
   * (unsend/reaction/hide) are not messages and are skipped.
   */
  putMessages(messages: IChat[], epoch?: string): Promise<void>;
  /** The room's newest `limit` messages, oldest first. */
  recentMessages(roomId: string, limit?: number): Promise<IChat[]>;
  /**
   * Rewrites one stored message in place (`null` removes it). A message that is not
   * stored is not called for: an event for something this device never saw is a no-op.
   */
  update(
    roomId: string,
    messageId: bigint,
    edit: (message: IChat) => IChat | null,
    epoch?: string
  ): Promise<void>;
  /**
   * A history page: authoritative for ids in `[from, to)`. Stored messages in that range
   * that `messages` lacks are dropped (hidden or unsent elsewhere); everything outside it
   * stays, so a message that arrived live after the request is safe.
   */
  putPage(
    roomId: string,
    messages: IChat[],
    range: { from: bigint; to: bigint },
    epoch?: string
  ): Promise<void>;
  /** The room's stored messages become exactly `messages` (a cleared history). */
  replaceRoom(roomId: string, messages: IChat[], epoch?: string): Promise<void>;
  /** Drops one room (the user left it). */
  forgetRoom(roomId: string, epoch?: string): Promise<void>;
  /** The room's stored messages with these ids (the ones it has). */
  messages(roomId: string, ids: bigint[]): Promise<IChat[]>;
  /** Every Encrypted message the local database holds that this Device could read (the History backup uploads them on getting its key). */
  readableEncrypted(): Promise<IChat[]>;
  /** Every MLS row this Device has saved (`mls_kv`), for the e2ee layer to load at start. */
  loadMls(): Promise<MlsRow[]>;
  /**
   * One MLS operation's changes and the plaintext messages it decrypted, in one transaction
   * (ADR-0017): the state moves with the message or not at all. `false` when nothing was
   * written (the session ended, or the database failed): the caller rolls the operation back.
   */
  putMls(changes: MlsChange[], messages?: IChat[], epoch?: string): Promise<boolean>;
  /**
   * Deletes the signed-in account's whole database, `mls_kv` included. Sign-out. Every
   * operation started before it, in this tab, is dropped.
   */
  wipe(): Promise<void>;
  /**
   * The session an operation starts in: changes when a wipe begins or the account
   * changes (a sign-out in another tab included). An operation given the epoch it
   * started in is dropped if the current one differs, whenever it gets to run. Without
   * one, an operation carries the epoch of the moment it was called.
   */
  epoch(): string;
}

/** One `mls_kv` row, as e2ee-core reports a change: `value` absent is a delete. */
export interface MlsChange {
  scope: string;
  key: Uint8Array;
  value?: Uint8Array;
}
export type MlsRow = Required<MlsChange>;

export const MESSAGES_STORE = "messages";
/**
 * The MLS client state (`scope`, hex `key`, raw `k` and `value`), written in the same
 * transaction as the messages it decrypted (ADR-0017). The key is hex because that is a key
 * IndexedDB sorts and compares the same everywhere; `k` is what e2ee-core gets back.
 */
export const MLS_KV_STORE = "mls_kv";

export const DB_NAME = "astring-local";
const DB_VERSION = 1;
export const DEFAULT_RECENT = 50;
/** A room keeps its newest messages only: the server has the rest. */
export const MAX_PER_ROOM = 300;
const OPEN_TIMEOUT_MS = 5000;

/**
 * IndexedDB keys cannot be bigints, and snowflakes outgrow a double. A fixed-width
 * decimal string sorts the way the number does.
 */
const idKey = (id: bigint) => id.toString().padStart(20, "0");

interface Row {
  roomId: string;
  id: string;
  message: IChat;
}

interface MlsRecord {
  scope: string;
  key: string;
  k: Uint8Array;
  value: Uint8Array;
}

const hex = (bytes: Uint8Array) => Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");

const asRow = (message: IChat): Row => ({
  roomId: message.roomId,
  id: idKey(BigInt(message.messageId)),
  message,
});

const settled = (tx: IDBTransaction) =>
  new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error("transaction aborted"));
  });

const result = <T>(req: IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

/**
 * Stores a message. An unreadable Encrypted one (this Device could not open it) is stored
 * without text and never replaces a stored readable copy: MLS opens a message once, so what
 * was kept when it arrived is the only text there will ever be.
 */
const putRow = async (store: IDBObjectStore, message: IChat) => {
  if (!message.unreadable) return void store.put(asRow(message));
  const kept = await result(store.get([message.roomId, idKey(BigInt(message.messageId))]) as IDBRequest<Row | undefined>);
  if (!kept || kept.message.unreadable) store.put(asRow({ ...message, message: "" }));
};

const roomRange = (roomId: string) => IDBKeyRange.bound([roomId, ""], [roomId, "￿"]);

const roomRangeBetween = (roomId: string, from: bigint, to: bigint) =>
  IDBKeyRange.bound([roomId, idKey(from)], [roomId, idKey(to)], false, true);

/** Deletes everything in `range` whose id is not in `keep`. */
const dropOutside = (store: IDBObjectStore, range: IDBKeyRange, keep: Set<string>) =>
  new Promise<void>((resolve, reject) => {
    const cursor = store.openCursor(range);
    cursor.onerror = () => reject(cursor.error);
    cursor.onsuccess = () => {
      const c = cursor.result;
      if (!c) return resolve();
      if (!keep.has((c.value as Row).id)) c.delete();
      c.continue();
    };
  });

/** Deletes a room's rows below its newest `cap`. */
const trimRoom = (store: IDBObjectStore, roomId: string, cap: number) =>
  new Promise<void>((resolve, reject) => {
    const cursor = store.openCursor(roomRange(roomId), "prev");
    let skipped = false;
    cursor.onerror = () => reject(cursor.error);
    cursor.onsuccess = () => {
      const c = cursor.result;
      if (!c) return resolve();
      if (!skipped) {
        skipped = true;
        if (cap > 0) return c.advance(cap);
      }
      c.delete();
      c.continue();
    };
  });

export interface MessageStoreOptions {
  /**
   * The database's name; with `account`, its prefix (`<name>:<accountId>`).
   * Defaults to `astring-local`.
   */
  name?: string;
  /**
   * The signed-in account, asked on every call. Given, the store is one database per
   * account and does nothing while it answers `undefined`. Absent, there is one
   * database, `name`.
   */
  account?: () => string | undefined;
  /** `null` is "no IndexedDB": every call is a quiet no-op. */
  factory?: IDBFactory | null;
  version?: number;
  maxPerRoom?: number;
  /** How long an open may wait on another tab before it is given up on. */
  openTimeoutMs?: number;
}

export function createMessageStore(options: MessageStoreOptions = {}): MessageStore {
  const base = options.name ?? DB_NAME;
  const account = options.account;
  const factory = options.factory === undefined ? globalThis.indexedDB : options.factory;
  const version = options.version ?? DB_VERSION;
  const cap = options.maxPerRoom ?? MAX_PER_ROOM;
  const openTimeout = options.openTimeoutMs ?? OPEN_TIMEOUT_MS;

  /** The database for the signed-in account, or `undefined`: nobody to store for. */
  const dbName = () => {
    if (!account) return base;
    const id = account();
    return id ? `${base}:${id}` : undefined;
  };

  let wipes = 0;
  const epoch = () => `${wipes}|${dbName() ?? ""}`;

  const dbs = new Map<string, Promise<IDBDatabase>>();
  /** Opens we stopped waiting for that have not come through yet: ask again only after. */
  const stalled = new Set<string>();
  const open = (name: string) => {
    let db = dbs.get(name);
    if (db) return db;
    if (stalled.has(name)) return Promise.reject(new Error("the database is still held by another tab"));
    db = new Promise<IDBDatabase>((resolve, reject) => {
      if (!factory) return reject(new Error("IndexedDB is unavailable"));
      const req = factory.open(name, version);
      let gaveUp = false;
      const giveUp = (error: unknown) => {
        gaveUp = true;
        stalled.add(name);
        clearTimeout(timer);
        dbs.delete(name);
        reject(error);
      };
      // Blocked, or queued behind another tab's delete: never hold the queue for it.
      const timer = setTimeout(() => giveUp(new Error("opening the database timed out")), openTimeout);
      req.onupgradeneeded = (event) => {
        const d = req.result;
        if (event.oldVersion < 1) {
          d.createObjectStore(MESSAGES_STORE, { keyPath: ["roomId", "id"] });
          d.createObjectStore(MLS_KV_STORE, { keyPath: ["scope", "key"] });
        }
      };
      req.onblocked = () => giveUp(new Error("opening the database is blocked by another tab"));
      req.onsuccess = () => {
        const d = req.result;
        if (gaveUp) {
          // The open we stopped waiting for finally came through.
          stalled.delete(name);
          return d.close();
        }
        clearTimeout(timer);
        // Another tab upgrading or deleting the database must not be blocked by us.
        d.onversionchange = () => {
          d.close();
          dbs.delete(name);
        };
        d.onclose = () => dbs.delete(name);
        resolve(d);
      };
      req.onerror = () => {
        if (!gaveUp) giveUp(req.error);
        stalled.delete(name);
      };
    });
    dbs.set(name, db);
    return db;
  };

  /** Deletes a database. Resolves when it is gone, or once it is only waiting on other tabs. */
  const drop = async (name: string) => {
    const cached = dbs.get(name);
    dbs.delete(name);
    if (cached) (await cached.catch(() => undefined))?.close();
    if (!factory) return;
    await new Promise<void>((resolve) => {
      const req = factory.deleteDatabase(name);
      req.onsuccess = req.onerror = () => resolve();
      // Other tabs close on `versionchange` and the delete then finishes by itself.
      req.onblocked = () => resolve();
    });
  };

  let queue: Promise<unknown> = Promise.resolve();
  const enqueue = <T>(fallback: T, started: string | undefined, op: (d: IDBDatabase) => Promise<T>) => {
    const startedIn = started ?? epoch();
    const next = queue.then(async () => {
      const name = dbName();
      // The session or the account changed since this was asked for: it is not ours to run.
      if (!name || epoch() !== startedIn) return fallback;
      try {
        return await op(await open(name));
      } catch (error) {
        if (factory) {
          dbs.delete(name);
          console.warn("message store failed", error);
        }
        return fallback;
      }
    });
    queue = next;
    return next;
  };

  const storable = (messages: IChat[]) => messages.filter((m) => !isMessageAction(m.type));

  const write = (epoch: string | undefined, rooms: (rows: IChat[]) => string[], messages: IChat[]) => {
    const rows = storable(messages);
    if (!rows.length) return Promise.resolve();
    return enqueue<void>(undefined, epoch, async (d) => {
      const tx = d.transaction(MESSAGES_STORE, "readwrite");
      const store = tx.objectStore(MESSAGES_STORE);
      for (const message of rows) await putRow(store, message);
      await Promise.all(rooms(rows).map((roomId) => trimRoom(store, roomId, cap)));
      await settled(tx);
    });
  };

  return {
    epoch,

    putMessages: (messages, started) =>
      write(started, (rows) => [...new Set(rows.map((m) => m.roomId))], messages),

    putPage: (roomId, messages, range, started) =>
      enqueue<void>(undefined, started, async (d) => {
        const tx = d.transaction(MESSAGES_STORE, "readwrite");
        const store = tx.objectStore(MESSAGES_STORE);
        const rows = storable(messages).map((m) => ({ ...m, roomId }));
        void dropOutside(
          store,
          roomRangeBetween(roomId, range.from, range.to),
          new Set(rows.map((m) => asRow(m).id))
        ).catch(() => tx.abort());
        for (const row of rows) await putRow(store, row);
        void trimRoom(store, roomId, cap).catch(() => tx.abort());
        await settled(tx);
      }),

    recentMessages: (roomId, limit = DEFAULT_RECENT) =>
      enqueue<IChat[]>([], undefined, async (d) => {
        const store = d.transaction(MESSAGES_STORE, "readonly").objectStore(MESSAGES_STORE);
        const rows: IChat[] = [];
        const cursor = store.openCursor(roomRange(roomId), "prev");
        await new Promise<void>((resolve, reject) => {
          cursor.onerror = () => reject(cursor.error);
          cursor.onsuccess = () => {
            const c = cursor.result;
            if (!c || rows.length >= limit) return resolve();
            rows.push((c.value as Row).message);
            c.continue();
          };
        });
        return rows.reverse();
      }),

    messages: (roomId, ids) =>
      enqueue<IChat[]>([], undefined, async (d) => {
        const store = d.transaction(MESSAGES_STORE, "readonly").objectStore(MESSAGES_STORE);
        const rows = await Promise.all(
          ids.map((id) => result(store.get([roomId, idKey(id)]) as IDBRequest<Row | undefined>))
        );
        return rows.flatMap((r) => (r ? [r.message] : []));
      }),

    readableEncrypted: () =>
      enqueue<IChat[]>([], undefined, async (d) => {
        const rows = await result(
          d.transaction(MESSAGES_STORE, "readonly").objectStore(MESSAGES_STORE).getAll() as IDBRequest<Row[]>
        );
        return rows.map((r) => r.message).filter((m) => m.encrypted === true && !m.unreadable);
      }),

    loadMls: () =>
      enqueue<MlsRow[]>([], undefined, async (d) => {
        const rows = await result(
          d.transaction(MLS_KV_STORE, "readonly").objectStore(MLS_KV_STORE).getAll() as IDBRequest<MlsRecord[]>
        );
        return rows.map((r) => ({ scope: r.scope, key: r.k, value: r.value }));
      }),

    putMls: (changes, messages = [], started) =>
      enqueue<boolean>(false, started, async (d) => {
        const tx = d.transaction([MLS_KV_STORE, MESSAGES_STORE], "readwrite");
        const kv = tx.objectStore(MLS_KV_STORE);
        for (const { scope, key, value } of changes) {
          if (value) kv.put({ scope, key: hex(key), k: key, value } satisfies MlsRecord);
          else kv.delete([scope, hex(key)]);
        }
        const rows = storable(messages);
        const store = tx.objectStore(MESSAGES_STORE);
        for (const message of rows) await putRow(store, message);
        await Promise.all([...new Set(rows.map((m) => m.roomId))].map((roomId) => trimRoom(store, roomId, cap)));
        await settled(tx);
        return true;
      }),

    update: (roomId, messageId, edit, started) =>
      enqueue<void>(undefined, started, async (d) => {
        const tx = d.transaction(MESSAGES_STORE, "readwrite");
        const store = tx.objectStore(MESSAGES_STORE);
        const key = [roomId, idKey(messageId)];
        const existing = await result(store.get(key) as IDBRequest<Row | undefined>);
        if (!existing) return;
        const next = edit(existing.message);
        if (next === null) store.delete(key);
        else store.put({ ...existing, message: next });
        await settled(tx);
      }),

    replaceRoom: (roomId, messages, started) =>
      enqueue<void>(undefined, started, async (d) => {
        const tx = d.transaction(MESSAGES_STORE, "readwrite");
        const store = tx.objectStore(MESSAGES_STORE);
        store.delete(roomRange(roomId));
        for (const message of storable(messages)) store.put(asRow({ ...message, roomId }));
        await settled(tx);
      }),

    forgetRoom: (roomId, started) =>
      enqueue<void>(undefined, started, async (d) => {
        const tx = d.transaction(MESSAGES_STORE, "readwrite");
        tx.objectStore(MESSAGES_STORE).delete(roomRange(roomId));
        await settled(tx);
      }),

    wipe: () => {
      const name = dbName();
      wipes++; // from here on, anything asked for earlier is dropped when its turn comes
      const next = queue.then(async () => {
        if (!name) return;
        try {
          await drop(name);
        } catch (error) {
          console.warn("message store wipe failed", error);
        }
      });
      queue = next;
      return next;
    },
  };
}

/** The app's store: one database per signed-in account. Tests that need an isolated one use `createMessageStore`. */
export const messageStore: MessageStore = createMessageStore({ account: signedInAccountId });
